import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, BookOpen, Clock, Filter, FlaskConical, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge, PublicationBadge } from '@/components/ui/Badge';
import { ButtonLink } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/Alert';
import { Input, Select } from '@/components/ui/Input';
import { SkeletonCard } from '@/components/ui/Spinner';
import { fetchers, queryKeys } from '@/services/queries';

export function CatalogPage() {
  const [search, setSearch] = useState('');
  const [trackFilter, setTrackFilter] = useState('');

  const tracks = useQuery({ queryKey: queryKeys.tracks, queryFn: fetchers.tracks });
  const modules = useQuery({
    queryKey: queryKeys.modules({ track: trackFilter || undefined }),
    queryFn: () => fetchers.modules(trackFilter ? { track: trackFilter } : undefined),
  });

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return modules.data ?? [];
    return (modules.data ?? []).filter(
      (module) =>
        module.title.toLowerCase().includes(needle) ||
        module.code.toLowerCase().includes(needle) ||
        module.summary.toLowerCase().includes(needle) ||
        module.technologies.some((tech) => tech.toLowerCase().includes(needle)),
    );
  }, [modules.data, search]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <header className="max-w-3xl">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Catalogue des formations</h1>
        <p className="mt-3 text-muted">
          Trois parcours professionnels, 28 modules, 252 heures. Chaque module affiche sa durée, ses
          objectifs, ses prérequis et les technologies manipulées.
        </p>
      </header>

      {/* Tracks */}
      <section className="mt-12" aria-labelledby="les-parcours">
        <h2 id="les-parcours" className="text-xl font-bold tracking-tight">
          Les parcours
        </h2>
        <div className="mt-5 grid gap-5 lg:grid-cols-3">
          {tracks.isLoading
            ? [0, 1, 2].map((index) => <SkeletonCard key={index} />)
            : tracks.data?.map((track) => (
                <Card key={track.id} interactive>
                  <CardBody>
                    <div className="flex items-center justify-between gap-2">
                      <Badge tone={track.code === 'A' ? 'tech' : track.code === 'B' ? 'ai' : 'agent'}>
                        Parcours {track.code}
                      </Badge>
                      <span className="text-xs font-semibold tabular-nums text-muted">
                        {track.duration_hours} h
                      </span>
                    </div>
                    <h3 className="mt-3 font-bold text-fg">{track.title}</h3>
                    <p className="mt-2 line-clamp-3 text-sm text-muted">{track.description}</p>
                    <dl className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-faint">
                      <div className="flex gap-1">
                        <dt>Niveau :</dt>
                        <dd className="font-medium text-muted">{track.level}</dd>
                      </div>
                      <div className="flex gap-1">
                        <dt>Modules :</dt>
                        <dd className="font-medium text-muted">
                          {track.published_module_count}/{track.module_count} ouverts
                        </dd>
                      </div>
                    </dl>
                    <Link
                      to={`/parcours/${track.slug}`}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-tech
                                 transition-all hover:gap-2.5"
                    >
                      Programme détaillé
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </CardBody>
                </Card>
              ))}
        </div>
      </section>

      {/* Modules */}
      <section className="mt-16" aria-labelledby="les-modules">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 id="les-modules" className="text-xl font-bold tracking-tight">
              Tous les modules
            </h2>
            <p className="mt-1 text-sm text-muted">
              {visible.length} module{visible.length > 1 ? 's' : ''} affiché
              {visible.length > 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex w-full flex-wrap items-end gap-3 sm:w-auto">
            <div className="relative min-w-[16rem] flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Rechercher un module, une technologie…"
                aria-label="Rechercher un module"
                className="pl-9"
              />
            </div>
            <Select
              value={trackFilter}
              onChange={(event) => setTrackFilter(event.target.value)}
              aria-label="Filtrer par parcours"
              className="min-w-[12rem]"
            >
              <option value="">Tous les parcours</option>
              {tracks.data?.map((track) => (
                <option key={track.id} value={track.slug}>
                  Parcours {track.code} — {track.title}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {modules.isLoading ? (
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {[0, 1, 2, 3].map((index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <EmptyState
            icon={<Filter className="mx-auto h-8 w-8" />}
            title="Aucun module ne correspond à votre recherche"
            description="Essayez un autre terme, ou retirez le filtre de parcours."
          />
        ) : (
          <ul className="mt-6 grid gap-4 md:grid-cols-2">
            {visible.map((module) => (
              <li key={module.id}>
                <Card interactive className="h-full">
                  <CardBody className="flex h-full flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Badge
                          tone={
                            module.track_code === 'A'
                              ? 'tech'
                              : module.track_code === 'B'
                                ? 'ai'
                                : 'agent'
                          }
                        >
                          {module.code}
                        </Badge>
                        <PublicationBadge status={module.status} />
                      </div>
                      <span className="flex items-center gap-1 text-xs font-semibold tabular-nums text-muted">
                        <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                        {module.duration_hours} h
                      </span>
                    </div>

                    <h3 className="mt-3 font-semibold text-fg">{module.title}</h3>
                    <p className="mt-2 flex-1 text-sm text-muted">{module.summary}</p>

                    {module.technologies.length > 0 ? (
                      <ul className="mt-4 flex flex-wrap gap-1.5">
                        {module.technologies.slice(0, 5).map((tech) => (
                          <li
                            key={tech}
                            className="rounded bg-surface-2 px-2 py-0.5 text-2xs font-medium text-muted"
                          >
                            {tech}
                          </li>
                        ))}
                        {module.technologies.length > 5 ? (
                          <li className="px-1 py-0.5 text-2xs text-faint">
                            +{module.technologies.length - 5}
                          </li>
                        ) : null}
                      </ul>
                    ) : null}

                    <div className="mt-4 flex items-center justify-between gap-3 border-t border-border pt-3">
                      <div className="flex gap-3 text-2xs text-faint">
                        <span className="flex items-center gap-1">
                          <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
                          {module.chapter_count} chapitre{module.chapter_count > 1 ? 's' : ''}
                        </span>
                        <span className="flex items-center gap-1">
                          <FlaskConical className="h-3.5 w-3.5" aria-hidden="true" />
                          {module.lab_count} TP
                        </span>
                      </div>
                      {module.status === 'published' ? (
                        <Link
                          to={`/modules/${module.code}`}
                          className="text-xs font-semibold text-tech hover:underline"
                        >
                          Consulter
                        </Link>
                      ) : (
                        <span className="text-2xs text-faint">Bientôt disponible</span>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="mt-16 rounded-card border border-border bg-surface/50 px-6 py-10 text-center">
        <h2 className="text-xl font-bold">Prêt à commencer ?</h2>
        <p className="mx-auto mt-2 max-w-lg text-sm text-muted">
          Connectez-vous à votre espace pour accéder aux cours, aux travaux pratiques et aux quiz des
          parcours auxquels vous êtes inscrit.
        </p>
        <ButtonLink to="/connexion" className="mt-6">
          Accéder à mon espace
        </ButtonLink>
      </div>
    </div>
  );
}
